// Generador de ligas etiquetadas (UTM) — sin backend. Arma la URL con
// utm_source / utm_medium / utm_campaign / utm_content y la copia.
// Así cada visita, registro y pago queda unido a su canal, campaña y pieza.
import React, { forwardRef, useMemo, useState } from 'react';
import { Copy, Check, Link2 } from 'lucide-react';

const CHANNELS: { key: string; label: string; source: string; medium: string }[] = [
  { key: 'whatsapp-grupo', label: 'WhatsApp · grupo', source: 'whatsapp', medium: 'grupo' },
  { key: 'whatsapp-contacto', label: 'WhatsApp · contacto', source: 'whatsapp', medium: 'contacto' },
  { key: 'facebook', label: 'Facebook', source: 'facebook', medium: 'social' },
  { key: 'instagram', label: 'Instagram', source: 'instagram', medium: 'social' },
  { key: 'google', label: 'Google', source: 'google', medium: 'cpc' },
  { key: 'email', label: 'Correo', source: 'email', medium: 'correo' },
  { key: 'otro', label: 'Otro', source: '', medium: '' },
];

const slug = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);

export function buildTaggedUrl(base: string, p: { source: string; medium: string; campaign: string; content?: string }): string {
  let u: URL;
  try {
    u = new URL(base.trim());
  } catch {
    return '';
  }
  if (!/^https?:$/.test(u.protocol)) return '';
  const set = (k: string, v?: string) => (v && slug(v) ? u.searchParams.set(k, slug(v)) : u.searchParams.delete(k));
  set('utm_source', p.source);
  set('utm_medium', p.medium);
  set('utm_campaign', p.campaign);
  set('utm_content', p.content);
  return u.toString();
}

export const UtmLinkBuilder = forwardRef<HTMLDivElement, { defaultUrl?: string; defaultCampaign?: string }>(
  ({ defaultUrl = '', defaultCampaign = '' }, ref) => {
    const [base, setBase] = useState(defaultUrl);
    const [channel, setChannel] = useState('whatsapp-grupo');
    const [source, setSource] = useState('');
    const [medium, setMedium] = useState('');
    const [campaign, setCampaign] = useState(defaultCampaign);
    const [content, setContent] = useState('');
    const [copied, setCopied] = useState(false);

    const ch = CHANNELS.find((c) => c.key === channel)!;
    const url = useMemo(
      () => buildTaggedUrl(base, { source: channel === 'otro' ? source : ch.source, medium: medium || ch.medium, campaign, content }),
      [base, channel, ch, source, medium, campaign, content],
    );
    const ready = Boolean(url && campaign.trim() && (channel !== 'otro' || source.trim()));

    const copy = async () => {
      if (!ready) return;
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch {
        setCopied(false);
      }
    };

    const input = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary';
    return (
      <div ref={ref} className="rounded-xl border border-border bg-card text-card-foreground p-4 md:p-5 min-w-0">
        <h3 className="font-semibold flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" />Liga etiquetada</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Cada canal con su propia liga: así sabemos de dónde viene cada visita, registro y pago.</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <label className="text-xs text-muted-foreground md:col-span-2">Página
            <input className={input} value={base} onChange={(e) => setBase(e.target.value)} placeholder="https://tu-sitio.mx/laboratorio-agentes" inputMode="url" />
          </label>
          <label className="text-xs text-muted-foreground">Canal
            <select className={input} value={channel} onChange={(e) => { setChannel(e.target.value); setMedium(''); }}>
              {CHANNELS.map((c) => <option key={c.key} value={c.key} className="bg-popover text-popover-foreground">{c.label}</option>)}
            </select>
          </label>
          {channel === 'otro' ? (
            <label className="text-xs text-muted-foreground">Fuente (utm_source)
              <input className={input} value={source} onChange={(e) => setSource(e.target.value)} placeholder="linkedin, volante, aliado…" />
            </label>
          ) : (
            <label className="text-xs text-muted-foreground">Medio (utm_medium)
              <input className={input} value={medium} onChange={(e) => setMedium(e.target.value)} placeholder={ch.medium} />
            </label>
          )}
          <label className="text-xs text-muted-foreground">Campaña (utm_campaign)
            <input className={input} value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="lab-agentes-nov" />
          </label>
          <label className="text-xs text-muted-foreground">Pieza / anuncio (utm_content, opcional)
            <input className={input} value={content} onChange={(e) => setContent(e.target.value)} placeholder="reel-1, carrusel-40-horas…" />
          </label>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <output className="flex-1 min-w-0 rounded-lg border border-border bg-muted/40 px-3 py-2 font-mono text-xs break-all text-foreground" aria-live="polite">
            {ready ? url : 'Escribe la página y la campaña para armar la liga.'}
          </output>
          <button type="button" onClick={copy} disabled={!ready}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? 'Copiada' : 'Copiar'}
          </button>
        </div>
      </div>
    );
  },
);
UtmLinkBuilder.displayName = 'UtmLinkBuilder';

export default UtmLinkBuilder;
